import React, { useEffect, useMemo, useRef, useState } from 'react';
import { safeArray, unwrapListResponse } from '../../components/ErrorBoundary';
import { useProjects } from '../../context/ProjectsContext';
import LinkedCorrespondenceCard from '../../components/LinkedCorrespondenceCard';
import ProjectFilterSelect from '../../components/ProjectFilterSelect';
import ResetFiltersButton from '../../components/ResetFiltersButton';
import StatusBadge from '../../components/StatusBadge';
import TableEmptyRow from '../../components/TableEmptyRow';
import { printTableElement } from '../../utils/exportUtils';
import { deviationsAPI } from '../../services/authService';
import { toStoredAttachments, downloadAttachment, viewAttachmentInNewTab } from '../../utils/attachmentStorage';
import { confirmDialog } from '../../utils/confirmDialog';
import { notifySuccess, notifyError } from '../../utils/notify';
import { formatDateIN } from '../../utils/dateFormat';

const SPECIALIZED_STATUS_CLASS_MAP = {
  Draft: 'draft',
  Submitted: 'submitted',
  'Under Review': 'under-review',
  Approved: 'approved',
  Rejected: 'rejected',
  Delivered: 'active',
  Closed: 'closed',
};

const SpecializedItems = () => {
  const [projectFilter, setProjectFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [fatFilter, setFatFilter] = useState('all');
  const [kpiFilter, setKpiFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [useApiData, setUseApiData] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const { projects: projectOptions } = useProjects();
  const [dataReady, setDataReady] = useState(false);
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState({
    project_id: '',
    item_name: '',
    spec_ref: '',
    vendor_name: '',
    country_of_origin: '',
    lead_time_days: '',
    expected_delivery_date: '',
    fat_required: 'No',
    warranty_months: '',
    special_instructions: '',
    attachments: [],
  });
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const tableRef = useRef(null);

  useEffect(() => {
    const loadItems = async () => {
      try {
        const response = await deviationsAPI.getAll({ page: 1, pageSize: 500 });
        const { rows: rawRows } = unwrapListResponse(response);
        const rows = safeArray(rawRows)
          .map((row) => {
            const payload = row.data || {};
            const isSpecialized = row.category === 'Specialized Item' || !!payload.item_name;
            if (!isSpecialized) return null;
            return {
              id: row.id,
              project_id: row.projectId ?? row.project_id ?? 1,
              item_name: String(payload.item_name || row.title || '').trim(),
              spec_ref: String(payload.spec_ref || payload.boq_item || row.deviation_no || '').trim(),
              vendor_name: String(payload.vendor_name || '').trim(),
              country_of_origin: String(payload.country_of_origin || '').trim(),
              lead_time_days: Number(payload.lead_time_days || 0),
              expected_delivery_date: payload.expected_delivery_date || null,
              fat_required: String(payload.fat_required || 'No'),
              warranty_months: Number(payload.warranty_months || 0),
              special_instructions: String(payload.special_instructions || payload.remarks || '').trim(),
              status: row.status || payload.status || 'Submitted',
              linked_correspondence: payload.linked_correspondence ?? row.linked_correspondence ?? null,
              attachments: payload.attachments ?? row.attachments ?? [],
            };
          })
          .filter(Boolean);
        setItems(rows);
        setUseApiData(true);
      } catch (error) {
        console.warn('Specialized Items API unavailable:', error?.message || error);
        setItems([]);
        setUseApiData(false);
      } finally {
        setDataReady(true);
      }
    };
    loadItems();
  }, []);

  const projects = safeArray(projectOptions);

  const getDaysToDelivery = (expectedDate) => {
    if (!expectedDate) return null;
    return Math.ceil((new Date(expectedDate) - new Date()) / (1000 * 60 * 60 * 24));
  };

  const filteredItems = useMemo(() => {
    let rows = [...items];
    if (projectFilter !== 'all') rows = rows.filter((item) => item.project_id === parseInt(projectFilter, 10));
    if (statusFilter !== 'all') rows = rows.filter((item) => item.status === statusFilter);
    if (fatFilter !== 'all') rows = rows.filter((item) => item.fat_required === fatFilter);
    if (kpiFilter === 'fatRequired') rows = rows.filter((item) => item.fat_required === 'Yes');
    if (kpiFilter === 'due30') {
      rows = rows.filter((item) => {
        const days = getDaysToDelivery(item.expected_delivery_date);
        return days !== null && days >= 0 && days <= 30;
      });
    }
    if (kpiFilter === 'overdue') {
      rows = rows.filter((item) => {
        const days = getDaysToDelivery(item.expected_delivery_date);
        return days !== null && days < 0;
      });
    }
    return rows;
  }, [items, projectFilter, statusFilter, fatFilter, kpiFilter]);

  const kpis = useMemo(() => {
    const total = filteredItems.length;
    const fatYes = filteredItems.filter((item) => item.fat_required === 'Yes').length;
    const dueIn30 = filteredItems.filter((item) => {
      const days = getDaysToDelivery(item.expected_delivery_date);
      return days !== null && days >= 0 && days <= 30;
    }).length;
    const overdue = filteredItems.filter((item) => {
      const days = getDaysToDelivery(item.expected_delivery_date);
      return days !== null && days < 0;
    }).length;
    return { total, fatYes, dueIn30, overdue };
  }, [filteredItems]);

  const getStatusClass = (status) => SPECIALIZED_STATUS_CLASS_MAP[status] || 'draft';

  if (!dataReady) {
    return (
      <div>
        <div className="section-header"><h2>Specialized Items</h2></div>
        <p style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>Loading...</p>
      </div>
    );
  }

  const handleCreateSpecializedItem = async () => {
    if (!newItem.project_id || !newItem.item_name.trim() || !newItem.spec_ref.trim() || !newItem.vendor_name.trim() || !newItem.lead_time_days || !newItem.expected_delivery_date) {
      notifyError('Project, item name, specification reference, vendor, lead time, and expected delivery date are required.');
      return;
    }
    if (Number(newItem.lead_time_days) <= 0) {
      notifyError('Lead time must be greater than zero.');
      return;
    }
    if (!(await confirmDialog('Create this specialized item?', 'New Specialized Item'))) return;

    const payloadData = {
      item_name: newItem.item_name.trim(),
      spec_ref: newItem.spec_ref.trim(),
      vendor_name: newItem.vendor_name.trim(),
      country_of_origin: newItem.country_of_origin.trim(),
      lead_time_days: Number(newItem.lead_time_days || 0),
      expected_delivery_date: newItem.expected_delivery_date || null,
      fat_required: newItem.fat_required,
      warranty_months: Number(newItem.warranty_months || 0),
      special_instructions: newItem.special_instructions.trim(),
      attachments: newItem.attachments || [],
    };

    if (useApiData) {
      try {
        const response = await deviationsAPI.create({
          projectId: Number(newItem.project_id),
          title: newItem.item_name.trim(),
          category: 'Specialized Item',
          status: 'Submitted',
          data: payloadData,
        });
        const row = response?.data;
        const rowPayload = row?.data || {};
        setItems((prev) => ([
          {
            id: row.id,
            project_id: row.projectId ?? Number(newItem.project_id),
            item_name: String(rowPayload.item_name || row.title || '').trim(),
            spec_ref: String(rowPayload.spec_ref || '').trim(),
            vendor_name: String(rowPayload.vendor_name || '').trim(),
            country_of_origin: String(rowPayload.country_of_origin || '').trim(),
            lead_time_days: Number(rowPayload.lead_time_days || 0),
            expected_delivery_date: rowPayload.expected_delivery_date || null,
            fat_required: String(rowPayload.fat_required || 'No'),
            warranty_months: Number(rowPayload.warranty_months || 0),
            special_instructions: String(rowPayload.special_instructions || '').trim(),
            status: row.status || 'Submitted',
            linked_correspondence: rowPayload.linked_correspondence ?? null,
            attachments: Array.isArray(rowPayload.attachments) ? rowPayload.attachments : (newItem.attachments || []),
          },
          ...prev,
        ]));
        notifySuccess('Specialized item created successfully.');
      } catch (error) {
        console.error('Create specialized item failed via API:', error);
        notifyError('Unable to create specialized item. Please retry.');
        return;
      }
    } else {
      setItems((prev) => ([
        {
          id: Math.max(0, ...prev.map((item) => Number(item.id) || 0)) + 1,
          project_id: Number(newItem.project_id),
          ...payloadData,
          status: 'Submitted',
          linked_correspondence: null,
          attachments: newItem.attachments || [],
        },
        ...prev,
      ]));
      notifySuccess('Specialized item created successfully.');
    }

    setShowModal(false);
    setNewItem({
      project_id: '',
      item_name: '',
      spec_ref: '',
      vendor_name: '',
      country_of_origin: '',
      lead_time_days: '',
      expected_delivery_date: '',
      fat_required: 'No',
      warranty_months: '',
      special_instructions: '',
      attachments: [],
    });
  };

  const handleAttachmentChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    setAttachmentUploading(true);
    try {
      const newFiles = await toStoredAttachments(files);
      setNewItem((prev) => ({ ...prev, attachments: [...(prev.attachments || []), ...newFiles] }));
    } finally {
      setAttachmentUploading(false);
    }
    event.target.value = '';
  };

  return (
    <div>
      <div className="section-header">
        <h2>Specialized Items Register <span className="count-badge">{filteredItems.length}</span></h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn-secondary"
            onClick={() => printTableElement({ title: 'Specialized Items Register - Filtered', subtitle: `Project: ${projectFilter === 'all' ? 'All Projects' : (projects.find((p) => p.id === parseInt(projectFilter, 10))?.name || 'Selected Project')}`, element: tableRef.current })}
          >
            Download PDF
          </button>
          <button className="btn-primary" onClick={() => setShowModal(true)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
            New Specialized Item
          </button>
        </div>
      </div>

      <div className="kpi-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 20 }}>
        <div className="kpi-card" onClick={() => setKpiFilter('all')} style={{ cursor: 'pointer', borderColor: kpiFilter === 'all' ? '#93c5fd' : undefined }}><h4>Total Items</h4><p className="kpi-value">{kpis.total}</p></div>
        <div className="kpi-card" onClick={() => setKpiFilter('fatRequired')} style={{ cursor: 'pointer', borderColor: kpiFilter === 'fatRequired' ? '#fcd34d' : undefined }}><h4>FAT Required</h4><p className="kpi-value">{kpis.fatYes}</p></div>
        <div className="kpi-card" onClick={() => setKpiFilter('due30')} style={{ cursor: 'pointer', borderColor: kpiFilter === 'due30' ? '#f59e0b' : undefined }}><h4>Due in 30 Days</h4><p className="kpi-value" style={{ color: '#b45309' }}>{kpis.dueIn30}</p></div>
        <div className="kpi-card" onClick={() => setKpiFilter('overdue')} style={{ cursor: 'pointer', borderColor: kpiFilter === 'overdue' ? '#fca5a5' : undefined }}><h4>Overdue Delivery</h4><p className="kpi-value" style={{ color: '#b91c1c' }}>{kpis.overdue}</p></div>
      </div>

      <div className="filter-bar">
        <ProjectFilterSelect value={projectFilter} onChange={setProjectFilter} projects={projects} />
        <select value={fatFilter} onChange={(e) => setFatFilter(e.target.value)}>
          <option value="all">All FAT</option>
          <option value="Yes">FAT Required</option>
          <option value="No">FAT Not Required</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All Status</option>
          <option value="Draft">Draft</option>
          <option value="Submitted">Submitted</option>
          <option value="Under Review">Under Review</option>
          <option value="Approved">Approved</option>
          <option value="Rejected">Rejected</option>
          <option value="Delivered">Delivered</option>
          <option value="Closed">Closed</option>
        </select>
        <ResetFiltersButton
          onClick={() => {
            setProjectFilter('all');
            setFatFilter('all');
            setStatusFilter('all');
            setKpiFilter('all');
          }}
        />
      </div>

      <div className="table-container" ref={tableRef}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>Specification Ref</th>
              <th>Vendor</th>
              <th>Lead Time</th>
              <th>Expected Delivery</th>
              <th>FAT</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredItems.length === 0 ? (
              <TableEmptyRow colSpan={7} message="No specialized items found." />
            ) : (
              filteredItems.map((item) => {
                const daysToDelivery = getDaysToDelivery(item.expected_delivery_date);
                return (
                  <tr key={item.id} className="clickable" onClick={() => setSelectedItem(item)}>
                    <td style={{ fontWeight: 500 }}>{item.item_name || '-'}</td>
                    <td>{item.spec_ref || '-'}</td>
                    <td>{item.vendor_name || '-'}</td>
                    <td>{item.lead_time_days || 0} days</td>
                    <td style={{ color: daysToDelivery !== null && daysToDelivery < 0 ? '#b91c1c' : '#374151' }}>
                      {item.expected_delivery_date || '-'}
                    </td>
                    <td><StatusBadge tone={item.fat_required === 'Yes' ? 'high' : 'closed'}>{item.fat_required}</StatusBadge></td>
                    <td><StatusBadge tone={getStatusClass(item.status)}>{item.status}</StatusBadge></td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {selectedItem && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Specialized Item Detail</h2>
              <button className="btn-close" onClick={() => setSelectedItem(null)}>
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <LinkedCorrespondenceCard linkedCorrespondenceId={selectedItem.linked_correspondence} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Item Name / Description</p><p style={{ fontSize: 14, fontWeight: 600 }}>{selectedItem.item_name || '-'}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Specification Reference</p><p style={{ fontSize: 14 }}>{selectedItem.spec_ref || '-'}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Manufacturer / Vendor</p><p style={{ fontSize: 14 }}>{selectedItem.vendor_name || '-'}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Country of Origin</p><p style={{ fontSize: 14 }}>{selectedItem.country_of_origin || '-'}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Lead Time</p><p style={{ fontSize: 14 }}>{selectedItem.lead_time_days || 0} days</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Expected Delivery Date</p><p style={{ fontSize: 14 }}>{formatDateIN(selectedItem.expected_delivery_date)}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>FAT Required</p><p><StatusBadge tone={selectedItem.fat_required === 'Yes' ? 'high' : 'closed'}>{selectedItem.fat_required}</StatusBadge></p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Warranty Period</p><p style={{ fontSize: 14 }}>{selectedItem.warranty_months || 0} months</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Status</p><p><StatusBadge tone={getStatusClass(selectedItem.status)}>{selectedItem.status}</StatusBadge></p></div>
              </div>
              {selectedItem.special_instructions && (
                <div style={{ marginTop: 16 }}>
                  <p style={{ fontSize: 12, color: '#6b7280' }}>Special Instructions</p>
                  <p style={{ fontSize: 13, color: '#374151' }}>{selectedItem.special_instructions}</p>
                </div>
              )}
              {(selectedItem.attachments || []).length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>Attachments</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                    {(selectedItem.attachments || []).map((file, idx) => (
                      <span key={idx} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <span style={{ fontSize: 13, color: '#374151' }}>{file?.name || `Attachment ${idx + 1}`}</span>
                        <button type="button" className="btn-secondary btn-sm" onClick={() => viewAttachmentInNewTab(file, () => downloadAttachment(file, `Specialized item attachment: ${file?.name}`))}>View</button>
                        <button type="button" className="btn-secondary btn-sm" onClick={() => downloadAttachment(file, `Specialized item attachment: ${file?.name}`)}>Download</button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>New Specialized Item</h2>
              <button className="btn-close" onClick={() => setShowModal(false)}>
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Project <span className="req">*</span></label>
                  <select value={newItem.project_id} onChange={(e) => setNewItem((prev) => ({ ...prev, project_id: e.target.value }))}>
                    <option value="">Select Project</option>
                    {projects.filter((p) => p.status === 'Active').map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>FAT Required <span className="req">*</span></label>
                  <select value={newItem.fat_required} onChange={(e) => setNewItem((prev) => ({ ...prev, fat_required: e.target.value }))}>
                    <option value="No">No</option>
                    <option value="Yes">Yes</option>
                  </select>
                </div>
              </div>
              <div className="form-group"><label>Item Name / Description <span className="req">*</span></label><input type="text" value={newItem.item_name} onChange={(e) => setNewItem((prev) => ({ ...prev, item_name: e.target.value }))} /></div>
              <div className="form-row">
                <div className="form-group"><label>Specification Reference <span className="req">*</span></label><input type="text" value={newItem.spec_ref} onChange={(e) => setNewItem((prev) => ({ ...prev, spec_ref: e.target.value }))} /></div>
                <div className="form-group"><label>Manufacturer / Vendor <span className="req">*</span></label><input type="text" value={newItem.vendor_name} onChange={(e) => setNewItem((prev) => ({ ...prev, vendor_name: e.target.value }))} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Country of Origin</label><input type="text" value={newItem.country_of_origin} onChange={(e) => setNewItem((prev) => ({ ...prev, country_of_origin: e.target.value }))} /></div>
                <div className="form-group"><label>Warranty Period (Months)</label><input type="number" value={newItem.warranty_months} onChange={(e) => setNewItem((prev) => ({ ...prev, warranty_months: e.target.value }))} placeholder="0" /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Lead Time (Days) <span className="req">*</span></label><input type="number" value={newItem.lead_time_days} onChange={(e) => setNewItem((prev) => ({ ...prev, lead_time_days: e.target.value }))} placeholder="0" /></div>
                <div className="form-group"><label>Expected Delivery Date <span className="req">*</span></label><input type="date" value={newItem.expected_delivery_date} onChange={(e) => setNewItem((prev) => ({ ...prev, expected_delivery_date: e.target.value }))} /></div>
              </div>
              <div className="form-group">
                <label>Special Instructions</label>
                <textarea rows={3} value={newItem.special_instructions} onChange={(e) => setNewItem((prev) => ({ ...prev, special_instructions: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Attachments</label>
                <input type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" onChange={handleAttachmentChange} disabled={attachmentUploading} />
                {attachmentUploading && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 13, color: '#6b7280' }}>
                    <span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid #e5e7eb', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    Processing upload...
                  </span>
                )}
                {newItem.attachments?.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                    {newItem.attachments.map((file, idx) => (
                      <span key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '4px 8px' }}>
                        {file?.name}
                        <span style={{ color: '#22c55e' }}>✓</span>
                        <button type="button" className="btn-secondary btn-sm" onClick={() => setNewItem((prev) => ({ ...prev, attachments: (prev.attachments || []).filter((_, i) => i !== idx) }))}>✕</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button className="btn-primary" onClick={handleCreateSpecializedItem} disabled={attachmentUploading}>Create Item</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpecializedItems;
